/**
 * Трёхступенчатая проверка открытости приложения.
 *
 * Функция определяет, открыто ли приложение прямо сейчас и если да, то по какому
 * признаку. Порядок ступеней важен: первая срабатывает независимо от флага,
 * вторая и третья требуют enableOpenAppSuppress.
 *
 * Ступени по приоритету:
 * 1. `focused-client` — есть сфокусированная вкладка. Проверяется всегда.
 * 2. `visible-client` — вкладка на экране, но фокус в другом приложении.
 *    Включается флагом enableOpenAppSuppress.
 * 3. `presence-beacon` — клиентов не видно вовсе, но страница недавно отмечалась
 *    живой (маяк свежее TTL). Это страховка, особенно для iOS. Требует флага.
 *
 * Каждая ступень возвращает своё значение: по ним потом видно, какая из них
 * реально срабатывает на какой платформе. На платформах с честным matchAll
 * (Chrome, Firefox) решение принимается по живым данным клиентов; на iOS,
 * где список клиентов недостоверен, мы падаем на маяк как последнее средство.
 */

export type AppOpenReason = 'focused-client' | 'visible-client' | 'presence-beacon';

export interface GetAppOpenReasonInput {
    /** Массив видимых в воркере клиентов: {focused?: boolean; visibilityState?: string} */
    clients: Array<{ focused?: boolean; visibilityState?: string }>;
    /** Timestamp: когда страница в последний раз отмечалась видимой (маяк) */
    visibleAt?: number;
    /** Текущее время для расчёта возраста маяка */
    now: number;
    /** Флаг для включения ступеней 2 и 3; при false только ступень 1 */
    enableOpenAppSuppress: boolean;
    /** TTL маяка в миллисекундах: маяк считается свежим если (now - visibleAt) < presenceTtlMs */
    presenceTtlMs: number;
}

/**
 * Узнать, открыто ли приложение и по какому признаку.
 *
 * Возвращает причину открытости приложения по приоритету:
 * 1. Сфокусированный клиент (всегда)
 * 2. Видимый клиент (если флаг включён)
 * 3. Свежий маяк (если флаг включён и маяк есть)
 *
 * Если приложение не открыто, возвращает null.
 */
export function getAppOpenReason(input: GetAppOpenReasonInput): AppOpenReason | null {
    const { clients, visibleAt, now, enableOpenAppSuppress, presenceTtlMs } = input;

    // Ступень 1: проверить сфокусированный клиент (безусловная).
    // Проверяется независимо от флага, потому что это существующее поведение.
    const focusedClient = clients.some((client) => client.focused === true);

    if (focusedClient) {
        return 'focused-client';
    }

    // Флаг enableOpenAppSuppress выключен: вести себя как сегодня (только ступень 1).
    if (!enableOpenAppSuppress) {
        return null;
    }

    // Ступень 2: проверить видимый несфокусированный клиент.
    const visibleClient = clients.some((client) => client.visibilityState === 'visible');

    if (visibleClient) {
        return 'visible-client';
    }

    // Ступень 3: проверить маяк. Маяк свежее TTL, если (now - visibleAt) < presenceTtlMs.
    // visibleAt может быть undefined: если страница ещё ни разу не была видима,
    // маяк не отрабатывает, возвращаем null.
    if (visibleAt !== undefined) {
        const age = now - visibleAt;

        if (age < presenceTtlMs) {
            return 'presence-beacon';
        }
    }

    return null;
}
